import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ensure } from 'components/sharpr/ensure/ensure';
import _ from 'lodash';

let prefs = {};
let watchers = [];

/** @ignore */
export function CfgResolves(resolves) {
	resolves.__cfgPreferences = ApiResourcify.endpoint('/config/preferences', {
		withResponse: resp => {
			CfgService.apply(ensure.object(resp.data));
		},
		cacheFor: 5 * 60 * 1000,
	});
}

/**
 * Retrieve and set configuration items using their name
 */
export class CfgService {
	/**
	 * @param {Api} Api	Instance of Api Service for making XHR requests
	 * @returns {undefined}
	 */
	constructor(Api) {
		'ngInject';
		/**
		 * The Api Service
		 * @type {Api}
		 */
		this.Api = Api;
		this.apply = CfgService.apply;
		this.watch = CfgService.watch;
		this.unwatch = CfgService.unwatch;
	}

	/**
	 * Returns the current context's preferences object (which acts as a list of all current preferences).
	 * @returns {{}}
	 */
	getPrefs() {
		return prefs;
	}

	/**
	 * Retrieve a configuration item using its name
	 * @param {String} code	Name of the configuration item
	 * @returns {*}	The configuration item referred to by the supplied name
	 */
	read(code) {
		return prefs[code];
	}

	/**
	 * Read a preference to check if it is 1 or true
	 * @param {String} code  Name of configuration item
	 * @returns {Boolean}
	 */
	readAsBool(code) {
		return prefs[code] === 1 || prefs[code] === '1' || prefs[code] === 'true' || prefs[code] === true;
	}

	/**
	 * Read a preference and cast as integer
	 * @param {String} code  Name of configuration item
	 * @returns {Number}
	 */
	readAsInt(code) {
		return parseInt(prefs[code], 10) || 0;
	}

	/**
	 * Temporarily store a value but don't save to the database
	 * @param {String} code  Name of the configuration item
	 * @param {String} value  Value of configuration item
	 * @returns {CfgService}  This object
	 * @chainable
	 */
	store(code, value) {
		prefs[code] = value;
		return this;
	}

	/**
	 * Set a configuration item using its name
	 * @param {String} code	Name of the configuration item to set
	 * @param {String} value	Value of configuration item
	 * @param {String} scope	Name of the scope in which to set the configuration item
	 * @returns {*}	Return output of Api patch request
	 */
	write(code, value, scope = 'user') {
		value = String(value);
		prefs[code] = value;
		CfgService.applyDocClasses();
		return this.Api.patch('/config/set', { code, value, scope });
	}

	load(hub_id) {
		const options = { avoidLoadingBar: true, hubs: hub_id };
		return this.Api.get(`/config/preferences`, {}, options).then(data => {
			this.apply(data);
		});
	}

	static apply(data) {
		prefs = data;
		CfgService.applyDocClasses();
		watchers.forEach(cb => cb(prefs));
	}

	static watch(callback) {
		watchers.push(callback);
		callback(prefs);
	}

	static unwatch(callback) {
		watchers = watchers.filter(cb => cb != callback);
	}

	static applyDocClasses() {
		AssetManager.removeDocClass('app-solution-*');
		AssetManager.removeDocClass('feature-*');
		if (!prefs || typeof prefs !== 'object') {
			return;
		}
		AssetManager.addDocClass('app-solution-' + prefs['App.solution']);
		_.forOwn(prefs, (value, name) => {
			if (name.match(/^Features?\./)) {
				name = name
					.toLowerCase()
					.replace(/^features?\./, '')
					.replace(/[^0-9a-z-]+/g, '-');
				value = value.toLowerCase().replace(/[^0-9a-z-]+/g, '-');
				AssetManager.addDocClass(`feature-${name}-${value}`);
			}
		});
	}
}
